import { useMemo, useState } from "react";
import { useMutation } from "@apollo/client";
import EmploymentTable from "@/features/person/components/EmploymentTable";
import { showSnackbar } from "@/lib/snackbarUtils";
import { useErrorBanner } from "@/providers/ErrorBannerProvider";
import type { EmploymentRow } from "@/pages/people/[pgaId]/person/queries";
import { WorkingLocation } from "@/types";
import { UPDATE_SUBMITTED_APPLICATION_MUTATION } from "../mutations";
import type { ApplicationEmployment } from "../queries";
import { MEMBERSHIP_APPLICATION_QUERY } from "../queries";
import { canEditApplicationFields } from "../utils";
import UpdateEmploymentClassificationAndTitleModal from "./UpdateEmploymentClassificationAndTitleModal";
import type { UpdateEmploymentClassificationAndTitleValues } from "./UpdateEmploymentClassificationAndTitleModal";

function toEmploymentRows(employments: ApplicationEmployment[]): EmploymentRow[] {
  return employments.map((e) => ({
    id: e.id,
    title: e.title ?? "",
    formattedTitle: e.formattedTitle ?? undefined,
    jobClassification: e.jobClassification ?? "",
    startedOn: e.startedOn,
    endedOn: e.endedOn,
    isPrimary: e.isPrimary,
    workingLocation: (e.workingLocation ?? undefined) as WorkingLocation,
    affiliationSection: e.affiliationSection ?? undefined,
    tourCode: e.tourCode ?? undefined,
    employmentKind: e.employmentKind,
    facility: {
      id: e.facility.id,
      name: e.facility.name
    }
  }));
}

interface Props {
  applicationId: string;
  pgaId: string;
  status: string;
  employments: ApplicationEmployment[];
  onViewFacility: (facilityId: string) => void;
  onClickEmployment: (employmentId: string) => void;
}

export default function MemberApplicationEmploymentSection({
  applicationId,
  pgaId,
  status,
  employments,
  onViewFacility,
  onClickEmployment
}: Props) {
  const { setError, clearError } = useErrorBanner();
  const [editEmploymentId, setEditEmploymentId] = useState<string | null>(null);
  const canEdit = canEditApplicationFields({ status });

  const employmentRows = useMemo(() => toEmploymentRows(employments), [employments]);
  const editEmployment = employments.find((employment) => employment.id === editEmploymentId) ?? null;

  const [updateApplication, { loading }] = useMutation(UPDATE_SUBMITTED_APPLICATION_MUTATION, {
    refetchQueries: [{ query: MEMBERSHIP_APPLICATION_QUERY, variables: { id: applicationId } }],
    awaitRefetchQueries: true
  });

  const handleSave = async (values: UpdateEmploymentClassificationAndTitleValues) => {
    if (!editEmployment) return;

    try {
      const { data } = await updateApplication({
        variables: {
          input: {
            id: applicationId,
            memberEmploymentUpdate: {
              employmentId: editEmployment.id,
              jobClassification: values.jobClassification,
              jobTitle: values.jobTitle,
              tourCode: values.jobClassification === "A-3" ? (values.tourCode ?? null) : null,
              facilityId: values.facilityId ?? null
            }
          }
        }
      });

      const result = data?.updateSubmittedApplication;
      if (result?.success) {
        clearError();
        showSnackbar(result.message);
        setEditEmploymentId(null);
      } else {
        setError(result?.message || "Failed to update employment");
      }
    } catch (error) {
      setError(error instanceof Error ? error.message : "Failed to update employment");
    }
  };

  return (
    <>
      <EmploymentTable
        employments={employmentRows}
        loading={false}
        canEdit={canEdit}
        canAddEmployment={false}
        pgaId={pgaId}
        onViewFacility={onViewFacility}
        onClickEmployment={onClickEmployment}
        onEditEmployment={canEdit ? setEditEmploymentId : undefined}
        canEditEmployment={(employment) => canEdit && employment.employmentKind !== "NON_MEMBER"}
        refetchQueries={[MEMBERSHIP_APPLICATION_QUERY]}
      />

      {canEdit && editEmployment && (
        <UpdateEmploymentClassificationAndTitleModal
          open
          onClose={() => setEditEmploymentId(null)}
          programType="member"
          employer={editEmployment.facility.name}
          currentJobTitle={editEmployment.title}
          currentJobClassification={editEmployment.jobClassification}
          currentTourCode={editEmployment.tourCode}
          saving={loading}
          onSave={handleSave}
        />
      )}
    </>
  );
}
